import { useEffect, useMemo, useState } from 'react';
import { configurationService } from '../services/configurationService';
import { CadApplyField, CadRecord, CallDetails } from '../types/Call';
import { formatDateTime } from '../components/calldetails/cadRecordDisplay';

/** Tenant configuration keys (A3.Core.Constants.CadRecordPolicy). */
const APPLY_POLICY_KEY = 'CadApplyPolicy';
const APPLY_FIELDS_KEY = 'CadApplyFields';

export type CadApplyPolicy = 'FillEmpty' | 'Overwrite';

const ALL_FIELDS: CadApplyField[] = [
  'IncidentNumber',
  'CallType',
  'CaseType',
  'IncidentDate',
  'Location',
  'CallerName',
  'CallerNumber',
  'OperatorId',
  'Operator',
];

interface RowSpec {
  id: string;
  label: string;
  /** The call record fields this row writes; the operator row writes the alias and the display name together. */
  fields: CadApplyField[];
  cad: (record: CadRecord) => string | null | undefined;
  current: (call: CallDetails) => string | null | undefined;
  /** False when the two sides are in different formats and cannot be compared here (the incident date). */
  comparable: boolean;
}

const sameValue = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Each call record field's current value, for deciding which of a row's fields would actually change. */
const CURRENT_VALUE: Record<CadApplyField, (call: CallDetails) => string | null | undefined> = {
  IncidentNumber: (c) => c.incidentNumber,
  CallType: (c) => c.callType,
  CaseType: (c) => c.caseType,
  IncidentDate: (c) => c.callDate,
  Location: (c) => c.location,
  CallerName: (c) => c.callerName,
  CallerNumber: (c) => c.callerNumber,
  OperatorId: (c) => c.operatorId,
  Operator: (c) => c.operator,
};

/** Order and wording follow the mockup. */
const ROWS: RowSpec[] = [
  {
    id: 'operator',
    label: 'Operator',
    fields: ['OperatorId', 'Operator'],
    cad: (r) => r.callTaker,
    // Alias and name, so a row where only the name differs does not read "CTUCKER / CTUCKER".
    current: (c) =>
      c.operatorId && c.operator && !sameValue(c.operatorId, c.operator)
        ? `${c.operatorId} (${c.operator})`
        : c.operatorId || c.operator,
    comparable: true,
  },
  {
    id: 'callType',
    label: 'Call type',
    fields: ['CallType'],
    cad: (r) => r.nature,
    current: (c) => c.callType,
    comparable: true,
  },
  {
    id: 'incidentDate',
    label: 'Incident date and time',
    fields: ['IncidentDate'],
    cad: (r) => r.receivedAt,
    current: (c) => (c.callDate ? formatDateTime(c.callDate) : null),
    comparable: false,
  },
  {
    id: 'location',
    label: 'Location',
    fields: ['Location'],
    cad: (r) => r.location,
    current: (c) => c.location,
    comparable: true,
  },
  {
    id: 'callerName',
    label: 'Caller name',
    fields: ['CallerName'],
    cad: (r) => r.callerName,
    current: (c) => c.callerName,
    comparable: true,
  },
  {
    id: 'callerNumber',
    label: 'Callback number',
    fields: ['CallerNumber'],
    cad: (r) => r.callerPhone,
    current: (c) => c.callerNumber,
    comparable: true,
  },
  {
    id: 'caseType',
    label: 'Case type',
    fields: ['CaseType'],
    cad: (r) => r.nature,
    current: (c) => c.caseType,
    comparable: true,
  },
  {
    id: 'incidentNumber',
    label: 'Incident number',
    fields: ['IncidentNumber'],
    cad: (r) => r.eventId,
    current: (c) => c.incidentNumber,
    comparable: true,
  },
];

/**
 * The choices the CAD settings screen offers for `CadApplyFields`, from the same rows the apply
 * dialog renders, so the allowlist an agency edits and the fields a supervisor sees can never
 * describe different things. Grouped rather than listed field by field because the operator alias
 * and display name are one decision: allowing one without the other assigns a call taker by id
 * with no name, or a name with no id.
 */
export const CAD_APPLY_FIELD_GROUPS: { id: string; label: string; fields: CadApplyField[] }[] =
  ROWS.map(({ id, label, fields }) => ({ id, label, fields }));

/** Placeholder values the ingestion path writes when it has nothing; FillEmpty treats them as empty. */
const isEmptyValue = (value: string | null | undefined) => {
  const text = value?.trim();
  return !text || /^(unknown|not specified|n\/a)$/i.test(text);
};

/**
 * The agency's allowlist, as the server reads it: comma separated, case-insensitive. A blank value
 * allows nothing (the server refuses everything in that case, so no rows may be offered).
 */
const parseAllowedFields = (value: string): Set<CadApplyField> =>
  new Set(
    value
      .split(',')
      .map((part) => ALL_FIELDS.find((f) => f.toLowerCase() === part.trim().toLowerCase()))
      .filter((f): f is CadApplyField => f !== undefined)
  );

type SettingsState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; policy: CadApplyPolicy; allowed: Set<CadApplyField> };

export interface CadApplyPlanRow extends RowSpec {
  allowedFields: CadApplyField[];
  /**
   * The allowed fields that would change, and only these are sent. The CAD report carries only
   * the call taker's alias, so the operator name is written only when the alias changes with it
   * or the name is empty: a matching alias never replaces "Carl Tucker" with "CTUCKER", and never
   * claims an access change.
   */
  sendFields: CadApplyField[];
  cadValue: string | undefined;
  currentValue: string | undefined;
  /** Why the row cannot be applied, or null when it can. */
  blockedReason: string | null;
  /**
   * Whether the agency's policy selects this row on its own: every row that can be applied under
   * Overwrite, only the rows whose current value is empty under FillEmpty. The operator row's
   * display string falls back to the name; FillEmpty is about the alias, which is the access
   * control.
   */
  preselected: boolean;
}

export type CadApplyPlan =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; policy: CadApplyPolicy; rows: CadApplyPlanRow[] };

/**
 * What an apply can offer for this call and record: the agency's allowlist and policy, each row's
 * current and CAD values, which fields would actually change, and which rows the policy selects
 * on its own. The apply dialog and the assistant's action bar share this, so they never disagree
 * about what "N fields" means. The settings are read once per mount. A caller that already holds
 * a plan passes `enabled` false: nothing is read and the result stays loading, unused.
 */
export function useCadApplyPlan(
  callDetails: CallDetails,
  fields: CadRecord | undefined,
  enabled: boolean
): CadApplyPlan {
  const [settings, setSettings] = useState<SettingsState>({ status: 'loading' });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    Promise.all([
      configurationService.getConfiguration(APPLY_POLICY_KEY),
      configurationService.getConfiguration(APPLY_FIELDS_KEY),
    ])
      .then(([policy, allowed]) => {
        if (cancelled) return;
        const policyValue = policy.configValue?.trim();
        // No default: an unrecognized policy is a misconfiguration to show, not to guess past.
        if (policyValue !== 'FillEmpty' && policyValue !== 'Overwrite') {
          setSettings({
            status: 'error',
            message: `The agency's CAD apply policy "${policyValue ?? ''}" is not recognized. Ask an administrator to set it to FillEmpty or Overwrite.`,
          });
          return;
        }
        setSettings({
          status: 'ready',
          policy: policyValue,
          allowed: parseAllowedFields(allowed.configValue ?? ''),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setSettings({
            status: 'error',
            message: "The agency's CAD apply settings could not be loaded. Try again.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const rows: CadApplyPlanRow[] = useMemo(() => {
    if (settings.status !== 'ready' || !fields) return [];
    return ROWS.map((spec) => {
      const allowedFields = spec.fields.filter((f) => settings.allowed.has(f));
      const cadValue = spec.cad(fields)?.trim() || undefined;
      const currentValue = spec.current(callDetails)?.trim() || undefined;
      let sendFields = spec.comparable
        ? allowedFields.filter((f) => !sameValue(CURRENT_VALUE[f](callDetails), cadValue))
        : allowedFields;
      if (
        spec.id === 'operator' &&
        !sendFields.includes('OperatorId') &&
        !isEmptyValue(callDetails.operator)
      ) {
        sendFields = sendFields.filter((f) => f !== 'Operator');
      }
      let blockedReason: string | null = null;
      if (!cadValue) {
        blockedReason = 'Not in the CAD report';
      } else if (sendFields.length === 0) {
        blockedReason = 'Already the same';
      }
      const preselected =
        blockedReason === null &&
        (settings.policy === 'Overwrite' ||
          isEmptyValue(spec.id === 'operator' ? callDetails.operatorId : currentValue));
      return {
        ...spec,
        allowedFields,
        sendFields,
        cadValue,
        currentValue,
        blockedReason,
        preselected,
      };
    }).filter((row) => row.allowedFields.length > 0);
  }, [settings, fields, callDetails]);

  return useMemo<CadApplyPlan>(
    () => (settings.status === 'ready' ? { status: 'ready', policy: settings.policy, rows } : settings),
    [settings, rows]
  );
}
